interface PlaceResult {
  place_id: string
  name: string
  formatted_address: string
  rating?: number
  user_ratings_total?: number
  types: string[]
  photos?: Array<{
    photo_reference: string
    height: number
    width: number
    url?: string // Added url field for new API
  }>
  url?: string
  website?: string
  opening_hours?: {
    open_now: boolean
    weekday_text: string[]
  }
  formatted_phone_number?: string
  international_phone_number?: string
  vicinity?: string
  price_level?: number
  reviews?: Array<{
    author_name: string
    rating: number
    text: string
    time: number
  }>
  geometry?: {
    location: {
      lat: number
      lng: number
    }
  }
}

interface NearbySearchResult {
  results: PlaceResult[]
  status: string
}

interface PlaceDetailsResult {
  result: PlaceResult
  status: string
}

class GooglePlacesService {
  private apiKey: string
  private isLoaded = false
  private loadingPromise: Promise<void> | null = null
  private cache = new Map<string, { data: any, timestamp: number }>()
  private CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours
  
  // API call tracking
  private apiCallCounts = {
    nearbySearch: 0,
    textSearch: 0,
    placeDetails: 0,
    autocomplete: 0,
    geocoding: 0,
    reverseGeocoding: 0,
    totalCost: 0
  }

  constructor() {
    this.apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY || ''
    if (!this.apiKey) {
      console.warn('Google Places API key not found in environment variables')
    }
    
    // Log API stats every minute
    setInterval(() => {
      if (this.apiCallCounts.totalCost > 0) {
        console.log('💰 Google Places API Usage Summary:', {
          ...this.apiCallCounts,
          totalCost: `$${this.apiCallCounts.totalCost.toFixed(4)}`
        })
      }
    }, 60000)
  }

  /**
   * Dynamically load Google Maps JavaScript API
   */
  private async loadGoogleMapsAPI(): Promise<void> {
    if (this.isLoaded && window.google?.maps?.places) {
      return
    }

    // If already loading, return the existing promise
    if (this.loadingPromise) {
      return this.loadingPromise
    }

    // Create a new loading promise
    this.loadingPromise = new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
      if (existingScript) {
        // Script exists, wait for it to load
        const checkLoaded = () => {
          if (window.google?.maps?.places) {
            this.isLoaded = true
            this.loadingPromise = null
            resolve()
          } else {
            setTimeout(checkLoaded, 100)
          }
        }
        checkLoaded()
        return
      }

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&loading=async`
      script.async = true
      script.defer = true
      script.onload = () => {
        // Wait a bit for the API to fully initialize
        const waitForAPI = (attempts = 0) => {
          if (window.google?.maps?.places || attempts > 50) {
            this.isLoaded = true
            this.loadingPromise = null
            if (window.google?.maps?.places) {
              resolve()
            } else {
              reject(new Error('Google Maps API loaded but places library not available'))
            }
          } else {
            setTimeout(() => waitForAPI(attempts + 1), 100)
          }
        }
        waitForAPI()
      }
      script.onerror = () => {
        this.loadingPromise = null
        reject(new Error('Failed to load Google Maps API'))
      }
      document.head.appendChild(script)
    })

    return this.loadingPromise
  }

  /**
   * Search for places near given coordinates
   */
  async searchNearbyPlaces(lat: number, lng: number, radius = 50, keyword?: string): Promise<PlaceResult[]> {
    // Check cache first
    const cacheKey = `nearby:${lat.toFixed(6)}:${lng.toFixed(6)}:${radius}:${keyword || ''}`
    const cached = this.cache.get(cacheKey)
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log('🎯 Using cached nearby search results (Cost: $0.00)')
      return cached.data
    }

    try {
      await this.loadGoogleMapsAPI()

      return new Promise((resolve, reject) => {
        const map = new google.maps.Map(document.createElement('div'))
        const service = new google.maps.places.PlacesService(map)

        const allResults: PlaceResult[] = []
        let completedSearches = 0
        let totalSearches = 0

        // If we have a keyword, do a text search and skip nearby search
        if (keyword && keyword.trim()) {
          totalSearches++
          const textSearchRequest = {
            query: keyword,
            location: new google.maps.LatLng(lat, lng),
            radius: radius * 2 // Larger radius for text search
          }

          service.textSearch(textSearchRequest, (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && results) {
              // Log API call
              this.apiCallCounts.textSearch++
              this.apiCallCounts.totalCost += 0.032
              console.log('💰 API CALL: Text Search', {
                cost: '$0.032',
                query: keyword,
                location: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                resultsFound: results.length,
                totalCostSoFar: `$${this.apiCallCounts.totalCost.toFixed(4)}`
              })
              const filteredResults = results
                .filter(place => place.name && place.place_id)
                .slice(0, 15) // Get more results since we're not doing nearby search
                .map(place => ({
                  place_id: place.place_id!,
                  name: place.name!,
                  formatted_address: place.formatted_address || place.vicinity || '',
                  rating: place.rating,
                  user_ratings_total: place.user_ratings_total,
                  types: place.types || [],
                  photos: place.photos?.map(photo => {
                    let photoUrl = ''
                    if (typeof (photo as any).getUrl === 'function') {
                      photoUrl = (photo as any).getUrl({ maxWidth: 600, maxHeight: 400 })
                    }
                    return {
                      photo_reference: '', // Not used in new API
                      height: (photo as any).height || 400,
                      width: (photo as any).width || 400,
                      url: photoUrl
                    }
                  }).filter(photo => photo.url)
                }))

              allResults.push(...filteredResults)
            }

            completedSearches++
            checkCompletion()
          })
        } else {
          // Only do nearby search if there's no keyword
          totalSearches += 1
        }

        const checkCompletion = () => {
          if (completedSearches === totalSearches) {
            // Remove duplicates and sort by relevance
            const uniqueResults = allResults.filter((place, index, self) => 
              self.findIndex(p => p.place_id === place.place_id) === index
            )
            
            // Sort by rating and prominence, limit to top 15
            const sortedResults = uniqueResults
              .sort((a, b) => {
                // Prioritize places with ratings
                if (a.rating && !b.rating) return -1
                if (!a.rating && b.rating) return 1
                if (a.rating && b.rating) return b.rating - a.rating
                return 0
              })
              .slice(0, 15)

            // Cache the results before returning
            this.cache.set(cacheKey, { data: sortedResults, timestamp: Date.now() })
            resolve(sortedResults)
          }
        }

        // Only do nearby search if there's no keyword
        if (!keyword || !keyword.trim()) {
          const request: any = {
            location: new google.maps.LatLng(lat, lng),
            radius: radius
          }

          service.nearbySearch(request, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            // Log API call
            this.apiCallCounts.nearbySearch++
            this.apiCallCounts.totalCost += 0.032
            console.log('💰 API CALL: Nearby Search', {
              cost: '$0.032',
              location: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              radius: radius,
              keyword: keyword || 'none',
              resultsFound: results.length,
              totalCostSoFar: `$${this.apiCallCounts.totalCost.toFixed(4)}`
            })
            // Filter and process results
            const filteredResults = results
              .filter(place => place.name && place.place_id)
              .map(place => ({
                place_id: place.place_id!,
                name: place.name!,
                formatted_address: place.vicinity || '',
                rating: place.rating,
                user_ratings_total: place.user_ratings_total,
                types: place.types || [],
                photos: place.photos?.map(photo => {
                  let photoUrl = ''
                  if (typeof (photo as any).getUrl === 'function') {
                    photoUrl = (photo as any).getUrl({ maxWidth: 600, maxHeight: 400 })
                  }
                  return {
                    photo_reference: '', // Not used in new API
                    height: (photo as any).height || 400,
                    width: (photo as any).width || 400,
                    url: photoUrl
                  }
                }).filter(photo => photo.url) // Only include photos with valid URLs
              }))

            allResults.push(...filteredResults)
          }

          completedSearches++
          checkCompletion()
        })
        } else {
          // If no searches were queued, resolve immediately with empty results
          if (totalSearches === 0) {
            resolve([])
          }
        }
      })
    } catch (error) {
      console.error('Error searching places:', error)
      return []
    }
  }

  /**
   * Get detailed information about a specific place
   */
  async getPlaceDetails(placeId: string): Promise<PlaceResult | null> {
    // Check cache first
    const cacheKey = `details:${placeId}`
    const cached = this.cache.get(cacheKey)
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log('🎯 Using cached place details (Cost: $0.00)')
      return cached.data
    }

    try {
      await this.loadGoogleMapsAPI()

      return new Promise((resolve, reject) => {
        const map = new google.maps.Map(document.createElement('div'))
        const service = new google.maps.places.PlacesService(map)

        const request = {
          placeId: placeId,
          fields: [
            'place_id',
            'name',
            'formatted_address',
            'rating',
            'user_ratings_total',
            'types',
            'photos',
            'url',
            'website',
            'opening_hours',
            'utc_offset_minutes',
            'formatted_phone_number',
            'international_phone_number',
            'vicinity',
            'price_level',
            'reviews',
            'geometry'
          ]
        }

        service.getDetails(request, (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            // Log API call
            this.apiCallCounts.placeDetails++
            this.apiCallCounts.totalCost += 0.017
            console.log('💰 API CALL: Place Details', {
              cost: '$0.017',
              placeId: placeId,
              placeName: place.name,
              hasPhotos: place.photos?.length || 0,
              totalCostSoFar: `$${this.apiCallCounts.totalCost.toFixed(4)}`
            })
            // Process photos - handle the new API format
            let processedPhotos: any[] = []
            if (place.photos && place.photos.length > 0) {
              // Limit to first 5 photos to avoid Google API rate limits (429 errors)
              processedPhotos = place.photos.slice(0, 5).map(photo => {
                // The new API provides getUrl() method instead of photo_reference
                let photoUrl = ''
                if (typeof (photo as any).getUrl === 'function') {
                  // Moderate resolution to balance quality and API limits
                  photoUrl = (photo as any).getUrl({ maxWidth: 600, maxHeight: 400 })
                }
                
                return {
                  photo_reference: '', // Not used in new API
                  height: (photo as any).height || 400,
                  width: (photo as any).width || 400,
                  url: photoUrl // Store the actual URL
                }
              }).filter(photo => photo.url) // Only include photos with valid URLs
            }

            // Debug opening hours
            if (place.opening_hours) {
              console.log('🕐 Opening hours for', place.name, {
                isOpen: place.opening_hours.isOpen?.(),
                weekdayText: place.opening_hours.weekday_text,
                periods: (place.opening_hours as any).periods,
                utcOffset: (place as any).utc_offset_minutes
              })
            }

            const result: PlaceResult = {
              place_id: place.place_id!,
              name: place.name!,
              formatted_address: place.formatted_address!,
              rating: place.rating,
              user_ratings_total: place.user_ratings_total,
              types: place.types || [],
              photos: processedPhotos,
              url: place.url,
              website: place.website,
              opening_hours: place.opening_hours ? {
                open_now: place.opening_hours.isOpen?.() || false, // Use isOpen() method which checks place's local time
                weekday_text: place.opening_hours.weekday_text || []
              } : undefined,
              formatted_phone_number: place.formatted_phone_number,
              international_phone_number: place.international_phone_number,
              vicinity: place.vicinity,
              price_level: place.price_level,
              reviews: place.reviews?.map(review => ({
                author_name: review.author_name,
                rating: review.rating,
                text: review.text,
                time: review.time
              })),
              geometry: place.geometry ? {
                location: {
                  lat: place.geometry.location?.lat() || 0,
                  lng: place.geometry.location?.lng() || 0
                }
              } : undefined
            }
            // Cache the result before returning
            this.cache.set(cacheKey, { data: result, timestamp: Date.now() })
            resolve(result)
          } else {
            console.warn('Place details failed:', status)
            resolve(null)
          }
        })
      })
    } catch (error) {
      console.error('Error getting place details:', error)
      return null
    }
  }

  /**
   * Get current API usage statistics
   */
  getApiUsageStats() {
    return {
      ...this.apiCallCounts,
      totalCost: `$${this.apiCallCounts.totalCost.toFixed(4)}`
    }
  }

  /**
   * Reset API usage statistics
   */
  resetApiUsageStats() {
    this.apiCallCounts = {
      nearbySearch: 0,
      textSearch: 0,
      placeDetails: 0,
      autocomplete: 0,
      geocoding: 0,
      reverseGeocoding: 0,
      totalCost: 0
    }
    console.log('💰 API usage stats reset')
  }

  /**
   * Get photo URL from photo reference
   */
  getPhotoUrl(photoReference: string, maxWidth = 400): string {
    if (!this.apiKey) return ''
    return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=${maxWidth}&photoreference=${photoReference}&key=${this.apiKey}`
  }

  /**
   * Get all photos for a place
   * Fetches place details with all available photos
   */
  async getPlacePhotos(placeId: string): Promise<Array<{ url: string; width: number; height: number }>> {
    try {
      await this.loadGoogleMapsAPI()

      return new Promise((resolve) => {
        const map = new google.maps.Map(document.createElement('div'))
        const service = new google.maps.places.PlacesService(map)

        const request = {
          placeId: placeId,
          fields: ['photos'] // Only request photos to minimize API cost
        }

        service.getDetails(request, (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place?.photos) {
            // Log API call (cheaper since we're only requesting photos field)
            this.apiCallCounts.placeDetails++
            this.apiCallCounts.totalCost += 0.005 // Lower cost when fetching only photos
            console.log('💰 API CALL: Place Photos', {
              cost: '$0.005',
              placeId: placeId,
              photosFound: place.photos.length,
              totalCostSoFar: `$${this.apiCallCounts.totalCost.toFixed(4)}`
            })

            // Get all photos (not just first 5) for selection modal
            const photos = place.photos.map(photo => {
              let photoUrl = ''
              if (typeof (photo as any).getUrl === 'function') {
                // Get larger photos for selection modal
                photoUrl = (photo as any).getUrl({ maxWidth: 800, maxHeight: 600 })
              }

              return {
                url: photoUrl,
                width: (photo as any).width || 800,
                height: (photo as any).height || 600
              }
            }).filter(photo => photo.url)

            resolve(photos)
          } else {
            console.warn('No photos found for place:', placeId, status)
            resolve([])
          }
        })
      })
    } catch (error) {
      console.error('Error fetching place photos:', error)
      return []
    }
  }

  /**
   * Extract place ID from various Google Maps URL formats
   */
  extractPlaceIdFromUrl(url: string): string | null {
    const patterns = [
      /place_id=([a-zA-Z0-9_-]+)/,
      /data=.*?1s([a-zA-Z0-9_-]+)/,
      /maps\/place\/[^/]+\/[^/]*@[^/]+\/data=.*?3m1.*?1s([a-zA-Z0-9_-]+)/
    ]

    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) return match[1]
    }

    return null
  }

  /**
   * Auto-suggest place name based on coordinates
   * Returns the most likely place name for the given location
   */
  async autoSuggestPlaceName(lat: number, lng: number, keyword?: string): Promise<string | null> {
    try {
      const places = await this.searchNearbyPlaces(lat, lng, 100, keyword) // Larger radius for better coverage
      
      if (places.length > 0) {
        // Return the closest/most relevant place name
        const topPlace = places[0]
        console.log('🏢 Auto-suggested place:', topPlace.name)
        return topPlace.name
      }

      // Fallback: use reverse geocoding to get general location
      const locationInfo = await this.getLocationInfo(lat, lng)
      if (locationInfo?.town) {
        console.log('📍 Auto-suggested from geocoding:', locationInfo.town)
        return locationInfo.town
      }

      return null
    } catch (error) {
      console.error('Error auto-suggesting place name:', error)
      return null
    }
  }

  /**
   * Get location info (town/state) from coordinates using reverse geocoding
   */
  async getLocationInfo(lat: number, lng: number): Promise<{ town?: string, state?: string, country?: string } | null> {
    try {
      await this.loadGoogleMapsAPI()

      return new Promise((resolve) => {
        const geocoder = new google.maps.Geocoder()
        const latlng = { lat, lng }

        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            // Log API call
            this.apiCallCounts.reverseGeocoding++
            this.apiCallCounts.totalCost += 0.005
            console.log('💰 API CALL: Reverse Geocoding', {
              cost: '$0.005',
              location: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              purpose: 'getLocationInfo',
              totalCostSoFar: `$${this.apiCallCounts.totalCost.toFixed(4)}`
            })
            const addressComponents = results[0].address_components
            const locationInfo: { town?: string, state?: string, country?: string } = {}

            addressComponents.forEach(component => {
              if (component.types.includes('locality')) {
                locationInfo.town = component.long_name
              } else if (component.types.includes('administrative_area_level_1')) {
                locationInfo.state = component.long_name
              } else if (component.types.includes('country')) {
                locationInfo.country = component.long_name
              }
            })

            resolve(locationInfo)
          } else {
            console.warn('Reverse geocoding failed:', status)
            resolve(null)
          }
        })
      })
    } catch (error) {
      console.error('Error getting location info:', error)
      return null
    }
  }

  /**
   * Search for locations by text query
   */
  async searchLocation(query: string): Promise<any[]> {
    try {
      await this.loadGoogleMapsAPI()

      return new Promise((resolve) => {
        const service = new google.maps.places.AutocompleteService()
        
        service.getPlacePredictions(
          {
            input: query,
            types: ['geocode', 'establishment']
          },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              // Log API call
              this.apiCallCounts.autocomplete++
              this.apiCallCounts.totalCost += 0.00283 // $2.83 per 1000 requests
              console.log('💰 API CALL: Autocomplete', {
                cost: '$0.00283',
                query: query,
                predictionsFound: predictions.length,
                totalCostSoFar: `$${this.apiCallCounts.totalCost.toFixed(4)}`
              })
              resolve(predictions.map(prediction => ({
                place_id: prediction.place_id,
                name: prediction.structured_formatting.main_text,
                formatted_address: prediction.description
              })))
            } else {
              resolve([])
            }
          }
        )
      })
    } catch (error) {
      console.error('Error searching location:', error)
      return []
    }
  }

  /**
   * Resolve a selected autocomplete prediction to coordinates.
   * Keep this separate from searchLocation so we don't geocode every suggestion.
   */
  async getLocationDetails(placeId: string): Promise<any | null> {
    const cacheKey = `location:${placeId}`
    const cached = this.cache.get(cacheKey)

    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log('🎯 Using cached location details (Cost: $0.00)')
      return cached.data
    }

    try {
      await this.loadGoogleMapsAPI()

      return new Promise((resolve) => {
        const map = new google.maps.Map(document.createElement('div'))
        const service = new google.maps.places.PlacesService(map)

        service.getDetails(
          {
            placeId,
            fields: ['place_id', 'name', 'formatted_address', 'geometry']
          },
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
              this.apiCallCounts.placeDetails++
              this.apiCallCounts.totalCost += 0.017
              console.log('💰 API CALL: Place Details (selected location only)', {
                cost: '$0.017',
                placeId,
                placeName: place.name,
                totalCostSoFar: `$${this.apiCallCounts.totalCost.toFixed(4)}`
              })

              const result = {
                place_id: place.place_id || placeId,
                name: place.name || '',
                formatted_address: place.formatted_address || '',
                geometry: {
                  location: {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng()
                  }
                }
              }
              this.cache.set(cacheKey, { data: result, timestamp: Date.now() })
              resolve(result)
            } else {
              resolve(null)
            }
          }
        )
      })
    } catch (error) {
      console.error('Error getting selected location details:', error)
      return null
    }
  }

  /**
   * Get detailed location info for a photo including address and region
   */
  async getPhotoLocationInfo(lat: number, lng: number): Promise<{
    address?: string
    location?: {
      street?: string
      city?: string
      state?: string
      country?: string
      region?: string
      postalCode?: string
    }
  } | null> {
    try {
      await this.loadGoogleMapsAPI()

      return new Promise((resolve) => {
        const geocoder = new google.maps.Geocoder()
        const latlng = { lat, lng }

        geocoder.geocode({ location: latlng }, async (results, status) => {
          if (status === 'OK' && results && results[0]) {
            // Log API call
            this.apiCallCounts.reverseGeocoding++
            this.apiCallCounts.totalCost += 0.005
            console.log('💰 API CALL: Reverse Geocoding', {
              cost: '$0.005',
              location: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              purpose: 'getPhotoLocationInfo',
              totalCostSoFar: `$${this.apiCallCounts.totalCost.toFixed(4)}`
            })
            const result = results[0]
            const addressComponents = result.address_components
            
            const location: {
              street?: string
              city?: string
              state?: string
              country?: string
              region?: string
              postalCode?: string
            } = {}

            // Extract address components
            addressComponents.forEach(component => {
              const types = component.types
              
              if (types.includes('street_number') || types.includes('route')) {
                // Combine street number and route for full street address
                const currentStreet = location.street || ''
                location.street = currentStreet 
                  ? `${currentStreet} ${component.long_name}`.trim()
                  : component.long_name
              }
              if (types.includes('locality')) {
                location.city = component.long_name
              }
              if (types.includes('administrative_area_level_1')) {
                location.state = component.short_name // Use short name for state (e.g., VIC)
              }
              if (types.includes('country')) {
                location.country = component.short_name // Use short name for country (e.g., AU)
              }
              if (types.includes('postal_code')) {
                location.postalCode = component.long_name
              }
              // Look for region/area names
              if (types.includes('administrative_area_level_2') || 
                  types.includes('colloquial_area') || 
                  types.includes('neighborhood')) {
                if (!location.region) {
                  location.region = component.long_name
                }
              }
            })

            // Use our custom region finder for accurate regional assignment
            console.log('🌍 Using custom region finder for accurate region assignment...')
            const { findRegion } = await import('../services/regionFinder')
            const regionResult = findRegion(lat, lng)
            
            if (regionResult.region !== 'Unknown') {
              location.region = regionResult.region
              console.log(`✅ Region set to: ${location.region}`)
            } else {
              console.log('⚠️ Region not found in custom boundaries')
            }

            const photoLocationInfo = {
              address: result.formatted_address,
              location
            }

            console.log('📍 Photo location info:', photoLocationInfo)
            resolve(photoLocationInfo)
          } else {
            console.warn('Reverse geocoding failed:', status)
            resolve(null)
          }
        })
      })
    } catch (error) {
      console.error('Error getting photo location info:', error)
      return null
    }
  }
}

// Export singleton instance
export const googlePlacesService = new GooglePlacesService()
export type { PlaceResult }
